'use client';
import React, { useEffect, useRef } from 'react';

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <div
          ref={sectionRef}
          className="bg-theme-04 rounded-[40px] p-10 md:p-14 relative overflow-hidden"
          style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
            {/* Left content */}
            <div>
              <div className="mb-6">
                <div
                  className="bg-dark text-white text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
                  style={{ transform: 'rotate(-6deg)' }}
                >
                  Get Started
                </div>
              </div>
              <h2 className="text-[36px] md:text-[44px] font-bold text-dark leading-tight mb-5">
                Start connecting with top TikTok live talent today!
              </h2>
              <p className="text-[17px] text-dark/70 leading-relaxed mb-8">
                Whether you&apos;re a seller looking for brand deals or a brand looking for live commerce talent — Talent is where it happens.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="#"
                  className="font-bold text-[15px] text-white bg-dark px-8 py-4 rounded-pill hover:bg-opacity-80 transition-all duration-200 text-center"
                >
                  Join as a Seller
                </a>
                <a
                  href="#"
                  className="font-bold text-[15px] text-dark bg-white px-8 py-4 rounded-pill hover:bg-neutral-01 transition-all duration-200 text-center border border-dark/10"
                >
                  Hire a Seller
                </a>
              </div>
            </div>

            {/* Right - floating cards */}
            <div className="relative h-64 lg:h-auto">
              {/* Floating label */}
              <div className="absolute top-0 right-0 lg:top-4 lg:right-4">
                <div className="flex items-center gap-2">
                  <div className="bg-dark text-white text-[13px] font-semibold px-4 py-2 rounded-full">
                    Live Now
                  </div>
                  <div className="w-2 h-2 rounded-full bg-theme-green animate-pulse" />
                </div>
              </div>

              {/* Card 1 */}
              <div className="absolute top-8 left-0 lg:left-8 bg-white rounded-2xl p-4 shadow-lg w-52 animate-float">
                <div className="flex items-center gap-2 mb-2">
                  <img src="https://i.pravatar.cc/32?img=30" alt="Aria" className="w-8 h-8 rounded-full" />
                  <div>
                    <p className="text-[12px] font-bold text-dark">Aria Chen</p>
                    <p className="text-[10px] text-dark/50">Beauty Seller</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-theme-green bg-theme-green/10 px-2 py-0.5 rounded-full">Available</span>
                  <span className="text-[11px] font-bold text-dark">$320K/mo</span>
                </div>
              </div>

              {/* Card 2 */}
              <div className="absolute bottom-0 right-0 lg:bottom-4 lg:right-0 bg-dark rounded-2xl p-4 shadow-lg w-52" style={{ animationDelay: '1.5s' }}>
                <p className="text-[11px] font-semibold text-white/50 mb-1">New Match Found!</p>
                <p className="text-[13px] font-bold text-white mb-2">GlowCo Beauty → Aria Chen</p>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} width="10" height="10" viewBox="0 0 24 24" fill="#FFD86F">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                  <span className="text-[10px] text-white/60 ml-1">Perfect match</span>
                </div>
              </div>

              {/* Decorative text */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                <p className="font-kalam text-[20px] text-dark/20 whitespace-nowrap">Live Commerce</p>
                <p className="font-kalam text-[20px] text-dark/20 whitespace-nowrap">Marketplace</p>
              </div>
            </div>
          </div>

          {/* Background shape */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-dark/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-dark/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
      </div>
    </section>
  );
}
